// src/lib/intake/intakeFlow.js
//
// Ticket 061 (Advisor Intake I4). The page's state machine as a pure reducer.
// The page (IntakePage.js) owns one stable component tree and renders by `stage`
// (CLAUDE.md rule 12). Network calls are made by the page/intakeSession.js and
// reported back as actions; nothing here touches fetch, storage or React.
//
// Stages:
//   welcome       first screen (Start / Continue)
//   section1      one of the eight Section 1 screens (state.screen; 5.1 filing history is last)
//   gate          email code, shown only after the last Section 1 screen
//   saving        a call to the intake functions is in flight
//   section23     one of the Section 2 or 3 screens (state.screen), ticket 064; each
//                 Next saves that screen's answers first (the page does the call and
//                 reports back with screenSaved / screenSaveFailed)
//   results       ticket 069: live results (calculated by the server from the SAVED
//                 answers; writes nothing) and the explicit "Save my results". After the
//                 last "Your money" screen the person lands here, not on Review.
//   share         ticket 070: the screen where the client shares their saved results with
//                 the advisor linked to their record (or stops sharing). Reached from the
//                 Results rail; Back returns to Results. Same IntakeShell, no new component type.
//   review        ticket 066: every screen's status, the gaps, Fix/Edit links. Reached
//                 from Results ("Review your answers"); a Fix/Edit started here returns here
//   resume-gate   email code for "Continue" when signed out
//   resuming      looking for a saved intake after the code (or when signed in)
//   resume-pick   more than one saved intake: ask which year
//   resume-none   nothing found for that email (shown only AFTER the code)
//   failed        a call failed; answers kept; Retry offered
//   advisor       advisor accounts cannot fill this in yet (no redirect)
//   unavailable   the server switch is off (404)

import {
  SECTION1_SCREEN_ORDER,
  TAX_YEAR_SCREEN,
} from './intakeConfig'
import { emptyAnswers, emptyMove, digitsOnly, moveNeedsDay, pruneFacts, isScreenComplete } from './section1Facts'
import {
  emptyAnswers23, withAnswers23, allAnswersFromFacts, firstOpenScreen23, nextScreen23, previousScreen23,
  replaceCardItems, removeUntouched, removeScreenCards, CARD_SCREENS, isScreenComplete23,
} from './section23Facts'
import { EMPTY_CARDS_REMOVED } from './intakeConfig'
import { intakeTaxYearOptions } from './intakeYears'

export const FIRST_SCREEN = SECTION1_SCREEN_ORDER[0]
export const LAST_SCREEN = SECTION1_SCREEN_ORDER[SECTION1_SCREEN_ORDER.length - 1]

export function initialState(draft = null) {
  if (draft && draft.answers) {
    return {
      ...blank(),
      stage: 'section1',
      screen: SECTION1_SCREEN_ORDER.includes(draft.screen) ? draft.screen : FIRST_SCREEN,
      answers: withAnswers23(draft.answers),
      taxYear: draft.taxYear ?? null,
    }
  }
  return blank()
}

function blank() {
  return {
    stage: 'welcome',
    screen: FIRST_SCREEN,
    answers: { ...emptyAnswers(), ...emptyAnswers23() },
    taxYear: null,
    revision: null,
    notice: null,         // plain text shown above the screen
    failure: null,        // { where: 'submit' | 'resume', code }
    candidates: [],       // saved intakes found on resume
    suggestions: null,    // advice from the server (ticket 064); never stored
    dismissed: [],        // nudges the person dismissed, in memory only (P6)
    limitHit: false,      // the 20-item cap refused a card
    busy: false,          // a Section 2/3 save is in flight
    saveError: null,      // code of the last failed Section 2/3 save
    returnToReview: false, // set by Fix/Edit on Review; after the save, walk newly open screens then return
    fixFrom: 'review',    // ticket 069: where a Fix/Edit returns to: 'review' or 'results' (the "Not assessed" box)
    savedAnswers: null,   // ticket 069: the answers as last stored; Back from a Fix restores them (066 bug)
    results: null,        // ticket 069: the server's masked results for `revision`
    resultsStatus: 'idle', // idle | loading | ready | failed
    resultsError: null,   // code of the last failed calculation
    resultsSaving: false, // "Save my results" in flight
    saveResultsError: null,
    savedRevision: null,  // the revision frozen by the last successful Save (seeded from the share status)
    shareStatus: null,    // ticket 070: { advisorName, shared, sharedAt, savedRevision, newerSaved } or null
    shareStatusState: 'idle', // idle | loading | ready | unavailable (unavailable: no share entry)
    shareSending: false,  // a share or stop is in flight
    shareError: null,     // code of the last failed share or stop
  }
}

// The context the Section 2/3 helpers need.
const ctxOf = (state) => ({ answers: state.answers, suggestions: state.suggestions, dismissed: state.dismissed })

// The first screen (Section 1, then the money section) that still needs an answer,
// or null when everything is answered. The tax-year screen is never reopened (the
// year was needed to load) and the advice screen 3.4 is never "open". A skipped
// income is not open either, so it does not force a walk. This is the ONE rule for
// resume AND for the walk after a Fix/Edit (ticket 066, Q4).
export function firstOpenAny(state, today = new Date()) {
  for (const id of SECTION1_SCREEN_ORDER) {
    if (id === TAX_YEAR_SCREEN) continue
    if (!isScreenComplete(id, { answers: state.answers, taxYear: null, yearOptions: [], today })) return id
  }
  return firstOpenScreen23(ctxOf(state))
}

// Show `id` in the right stage. With none left: Review when this walk started from a
// Fix/Edit on Review, otherwise Results (ticket 069).
function goTo(state, id) {
  if (!id) {
    return {
      ...state,
      stage: state.returnToReview && state.fixFrom !== 'results' ? 'review' : 'results',
      screen: state.screen,
      returnToReview: false,
      notice: state.notice,
    }
  }
  const stage = SECTION1_SCREEN_ORDER.includes(id) ? 'section1' : 'section23'
  return { ...state, stage, screen: id }
}

export function screenIndex(id) {
  return SECTION1_SCREEN_ORDER.indexOf(id)
}

export function isLastScreen(id) {
  return id === LAST_SCREEN
}

function keepYearValid(state, today) {
  if (state.taxYear == null) return state
  const options = intakeTaxYearOptions(today, state.answers.move)
  return options.includes(state.taxYear) ? state : { ...state, taxYear: null }
}

function applyIntake(state, action) {
  const answers = allAnswersFromFacts(action.facts, action.today)
  const base = {
    ...state,
    answers,
    savedAnswers: answers,
    taxYear: action.taxYear,
    revision: action.revision,
    notice: action.notice || null,
    failure: null,
    candidates: [],
    suggestions: action.suggestions || null,
    dismissed: [],
    limitHit: false,
    busy: false,
    saveError: null,
    returnToReview: false,   // a reload (resume, 409) always starts from what is stored
    resultsSaving: false,    // a 409 on Save lands here: the Save is over, the page recalculates
    saveResultsError: null,
  }
  return goTo(base, firstOpenAny(base, action.today))
}

// Section 2/3 answer changes keep the screen and clear stale messages.
const changed = (state, answers, extra = {}) => ({ ...state, answers, notice: null, saveError: null, limitHit: false, ...extra })

// A changed revision means the facts changed: the old results no longer match them,
// so they are dropped and the page calculates again (ticket 069).
export function reduce(state, action) {
  const next = reduceInner(state, action)
  if (next.revision === state.revision || next.resultsStatus === 'idle') return next
  return { ...next, results: null, resultsStatus: 'idle', resultsError: null, saveResultsError: null }
}

function reduceInner(state, action) {
  const today = action.today || new Date()
  switch (action.type) {
    case 'begin':
      return { ...blank(), stage: 'section1', screen: FIRST_SCREEN }

    case 'restart':
      return blank()

    case 'continue':
      return { ...state, stage: action.signedIn ? 'resuming' : 'resume-gate', notice: null, failure: null }

    case 'setMove': {
      const move = { ...state.answers.move, ...action.patch }
      // Typed values bypass the HTML min/max, so the state itself keeps digits
      // only: year at most 4, day at most 2 (round 3).
      if ('year' in action.patch) move.year = digitsOnly(action.patch.year, 4)
      if ('day' in action.patch) move.day = digitsOnly(action.patch.day, 2)
      // The day exists only for July; any other month drops it.
      if (move.branch === 'recent' && !moveNeedsDay(move.month)) move.day = ''
      // The two checkboxes are mutually exclusive: choosing "before C" or
      // clearing the choice drops the date fields.
      if (action.patch.branch === 'before' || action.patch.branch === null) {
        Object.assign(move, { month: '', year: '', day: '' })
      }
      const next = { ...state, answers: { ...state.answers, move } }
      return keepYearValid(next, today)
    }

    case 'setFact': {
      let facts = { ...state.answers.facts, [action.key]: action.fact }
      // A part (under 3 / over 75) only exists while its total is a number above zero.
      if (action.key === 'children_under_25' && action.fact.status !== 'known_value') delete facts.children_under_3
      if (action.key === 'ascendants_over_65' && action.fact.status !== 'known_value') delete facts.ascendants_over_75
      facts = pruneFacts(facts)
      return { ...state, answers: { ...state.answers, facts } }
    }

    case 'clearFact': {
      const facts = { ...state.answers.facts }
      delete facts[action.key]
      return { ...state, answers: { ...state.answers, facts: pruneFacts(facts) } }
    }

    case 'setTaxYear':
      return { ...state, taxYear: action.year }

    case 'next': {
      // Section 2/3: only the advice screen has no save, so it is the only one
      // that moves on without a call.
      if (state.stage === 'section23') {
        if (state.screen !== '3.4') return state
        return goTo({ ...state, notice: null }, nextScreen23('3.4', ctxOf(state)))
      }
      if (state.stage !== 'section1') return state
      const ctx = {
        today,
        answers: state.answers,
        taxYear: state.taxYear,
        yearOptions: intakeTaxYearOptions(today, state.answers.move),
      }
      if (!isScreenComplete(state.screen, ctx)) return state
      return advance(state)
    }

    case 'back': {
      // From a Fix/Edit, Back returns to Review without saving, and the unsaved edit is
      // dropped: the answers go back to what is stored (the 066 bug, ticket 069).
      if (state.returnToReview && (state.stage === 'section1' || state.stage === 'section23')) {
        return {
          ...state,
          answers: state.savedAnswers || state.answers,
          stage: state.fixFrom === 'results' ? 'results' : 'review',
          returnToReview: false,
          notice: null,
          saveError: null,
          limitHit: false,
        }
      }
      if (state.stage === 'review') {
        return { ...state, stage: 'results', notice: null }
      }
      if (state.stage === 'share') {
        return { ...state, stage: 'results', notice: null, shareError: null }
      }
      if (state.stage === 'results') {
        return { ...state, stage: 'section23', screen: '4.1', notice: null }
      }
      if (state.stage === 'failed' && state.failure && state.failure.where === 'resume') {
        return { ...state, stage: 'welcome', failure: null }
      }
      if (state.stage === 'gate' || state.stage === 'failed') {
        return { ...state, stage: 'section1', screen: state.returnToReview ? state.screen : LAST_SCREEN, failure: null }
      }
      if (state.stage === 'resume-gate' || state.stage === 'resume-none' || state.stage === 'resume-pick') {
        return { ...blank(), answers: state.answers, taxYear: state.taxYear }
      }
      if (state.stage === 'section23') {
        const prev = previousScreen23(state.screen, ctxOf(state))
        if (prev) return { ...state, screen: prev, notice: null, saveError: null, limitHit: false }
        return { ...state, stage: 'section1', screen: LAST_SCREEN, notice: null, saveError: null, limitHit: false }
      }
      if (state.stage === 'section1') {
        const i = screenIndex(state.screen)
        return i <= 0 ? { ...state, stage: 'welcome', notice: null } : { ...state, screen: SECTION1_SCREEN_ORDER[i - 1], notice: null }
      }
      return state
    }

    case 'gatePassed':
    case 'submitting':
      return { ...state, stage: 'saving', notice: null, failure: null }

    case 'saved': {
      // Section 1 was saved (first save or an edit): go to the first screen that is
      // still open (only newly open ones after an edit), or to Review when all are done.
      const base = {
        ...state,
        revision: action.revision,
        savedAnswers: state.answers,
        suggestions: action.suggestions || state.suggestions,
        notice: null,
        failure: null,
      }
      // After a first save Section 1 is what was just sent (the Next button only lets
      // a complete screen through), so only the money screens can be open. After a
      // Fix/Edit, a Section 1 change can open a Section 1 screen too (marital status).
      return goTo(base, state.returnToReview ? firstOpenAny(base, today) : firstOpenScreen23(ctxOf(base)))
    }

    // Fix this / Edit on Review: open that screen. Next saves it (Section 1 is saved
    // as a whole), then the newly open screens are walked, then back to Review.
    case 'editScreen':
      return {
        ...state,
        stage: SECTION1_SCREEN_ORDER.includes(action.screen) ? 'section1' : 'section23',
        screen: action.screen,
        returnToReview: true,
        fixFrom: action.from === 'results' ? 'results' : 'review',
        notice: null,
        saveError: null,
        limitHit: false,
      }

    case 'setFiling': {
      const filing = { ...state.answers.filing, ...action.patch }
      // The gestor question exists only for "Yes".
      if (filing.filed !== true) filing.gestor = null
      return { ...state, answers: { ...state.answers, filing } }
    }

    case 'setAssets':
      return changed(state, { ...state.answers, assets: { ...state.answers.assets, [action.key]: action.value } })

    // ── Sections 2 and 3 (ticket 064) ───────────────────────────────────────
    // events and the "Owned property" follow-up change together (amendment 2)
    case 'setEvents':
      return changed(state, {
        ...state.answers,
        events: action.events,
        ownership: 'ownership' in action ? action.ownership : state.answers.ownership,
      })

    case 'setSkip': {
      // "Skip income for now": only offered while nothing is filled in, so it only
      // removes empty cards. It excludes "I had no income".
      const answers = action.value ? removeScreenCards(state.answers, '2.2') : state.answers
      return changed(state, { ...answers, skipIncome: !!action.value, none: { ...answers.none, self: action.value ? false : answers.none.self } })
    }

    case 'setCardItems': {
      const next = replaceCardItems(state.answers, action.cardType, action.owner, action.items)
      return next ? changed(state, next) : { ...state, limitHit: true }
    }

    case 'setNone': {
      const cfg = CARD_SCREENS[action.screen]
      if (!cfg) return state
      // The button is only offered when nothing in this group was filled in, so
      // choosing it only removes empty cards.
      const answers = action.value ? removeScreenCards(state.answers, action.screen) : state.answers
      return changed(state, {
        ...answers,
        none: { ...answers.none, [cfg.noneKey]: !!action.value },
        skipIncome: action.value && cfg.owner === 'self' ? false : answers.skipIncome,
      })
    }

    case 'setWorkLocation': {
      const workLocation = { ...state.answers.workLocation }
      if (action.value) workLocation[action.id] = action.value
      else delete workLocation[action.id]
      return changed(state, { ...state.answers, workLocation })
    }

    case 'setSpouseGate': {
      let answers = { ...state.answers, spouseHasIncome: action.value }
      if (action.value === false) {
        const keep = answers.incomeItems.filter((i) => i.owner !== 'spouse')
        const ids = new Set(keep.map((i) => i.id))
        const workLocation = {}
        for (const [id, v] of Object.entries(answers.workLocation)) if (ids.has(id)) workLocation[id] = v
        answers = {
          ...answers,
          incomeItems: keep,
          workLocation,
          none: { ...answers.none, spouse_work: false, spouse_invest: false },
        }
      }
      return changed(state, answers)
    }

    case 'setAutonomo':
      return changed(state, { ...state.answers, autonomo: action.autonomo })

    case 'setBeckham':
      return changed(state, { ...state.answers, beckham: action.value })

    case 'setSaleLocation':
      return changed(state, { ...state.answers, saleLocation: action.value })

    case 'dropUntouched': {
      const { answers, removed } = removeUntouched(state.answers, state.screen)
      return removed === 0 ? state : { ...state, answers, notice: EMPTY_CARDS_REMOVED(removed), limitHit: false }
    }

    case 'dismissNudge':
      return state.dismissed.includes(action.key) ? state : { ...state, dismissed: [...state.dismissed, action.key] }

    case 'screenSaving':
      return { ...state, busy: true, saveError: null }

    case 'screenSaveFailed':
      return { ...state, busy: false, saveError: action.code || 'UNKNOWN' }

    case 'screenSaved': {
      const base = {
        ...state,
        revision: action.revision,
        savedAnswers: state.answers,
        suggestions: action.suggestions || state.suggestions,
        busy: false,
        saveError: null,
        notice: null,
        limitHit: false,
      }
      const target = state.returnToReview ? firstOpenAny(base, today) : nextScreen23(state.screen, ctxOf(base))
      return goTo(base, target)
    }

    case 'applyIntake':
      return applyIntake(state, { ...action, today })

    // ── Results (ticket 069) ──────────────────────────────────────────────────
    case 'showResults':
      return state.stage === 'review' ? { ...state, stage: 'results', notice: null } : state

    case 'openReview':
      return state.stage === 'results' ? { ...state, stage: 'review', notice: null, returnToReview: false } : state

    case 'resultsLoading':
      return { ...state, resultsStatus: 'loading', resultsError: null }

    case 'resultsLoaded':
      return { ...state, results: action.results, resultsStatus: 'ready', resultsError: null }

    case 'resultsFailed':
      return { ...state, results: null, resultsStatus: 'failed', resultsError: action.code || 'UNKNOWN' }

    case 'resultsRetry':
      return { ...state, resultsStatus: 'idle', resultsError: null }

    case 'resultsSaving':
      return { ...state, resultsSaving: true, saveResultsError: null }

    case 'resultsSaved':
      // While sharing, a Save changes whether a newer version exists than the one named
      // at consent, so the status is read again (display only).
      return {
        ...state, resultsSaving: false, saveResultsError: null, savedRevision: action.revision,
        shareStatusState: state.shareStatus && state.shareStatus.shared ? 'idle' : state.shareStatusState,
      }

    case 'saveResultsFailed':
      return { ...state, resultsSaving: false, saveResultsError: action.code || 'UNKNOWN' }

    // ── Share with the advisor (ticket 070) ──────────────────────────────────
    case 'shareStatusLoading':
      return { ...state, shareStatusState: 'loading' }

    case 'shareStatusLoaded':
      return {
        ...state,
        shareStatus: action.status,
        shareStatusState: 'ready',
        // A returning client's "Saved" label comes from the newest saved version.
        savedRevision: action.status.savedRevision != null
          ? Math.max(state.savedRevision == null ? 0 : state.savedRevision, action.status.savedRevision)
          : state.savedRevision,
      }

    case 'shareStatusUnavailable':
      return { ...state, shareStatus: null, shareStatusState: 'unavailable' }

    case 'openShare':
      return state.stage === 'results' && state.shareStatus
        ? { ...state, stage: 'share', notice: null, shareError: null }
        : state

    case 'shareSending':
      return { ...state, shareSending: true, shareError: null }

    case 'shareDone': {
      const status = action.status
        || { ...(state.shareStatus || {}), shared: true, sharedAt: action.sharedAt || (state.shareStatus && state.shareStatus.sharedAt) || null, newerSaved: false }
      return { ...state, shareSending: false, shareError: null, shareStatus: status, shareStatusState: 'ready' }
    }

    case 'stopDone': {
      const status = action.status || { ...(state.shareStatus || {}), shared: false, sharedAt: null, newerSaved: false }
      return { ...state, stage: 'results', shareSending: false, shareError: null, shareStatus: status, shareStatusState: 'ready' }
    }

    case 'shareFailed':
      return { ...state, shareSending: false, shareError: action.code || 'UNKNOWN' }

    case 'resuming':
      return { ...state, stage: 'resuming', failure: null }

    case 'candidates': {
      const list = action.candidates
      if (list.length === 0) return { ...state, stage: 'resume-none', candidates: [] }
      if (list.length === 1) return applyIntake(state, { ...list[0], today })
      return { ...state, stage: 'resume-pick', candidates: list }
    }

    case 'pickCandidate': {
      const found = state.candidates.find((c) => c.taxYear === action.taxYear)
      return found ? applyIntake(state, { ...found, today }) : state
    }

    case 'failed':
      return { ...state, stage: 'failed', failure: { where: action.where, code: action.code } }

    case 'advisor':
      return { ...state, stage: 'advisor' }

    case 'unavailable':
      return { ...state, stage: 'unavailable' }

    case 'invalidYear':
      return { ...state, stage: 'section1', screen: TAX_YEAR_SCREEN, taxYear: null, notice: 'That tax year is not available. Please choose another.' }

    default:
      return state
  }
}

function advance(state) {
  const i = screenIndex(state.screen)
  if (isLastScreen(state.screen)) return { ...state, stage: 'gate', notice: null }
  return { ...state, screen: SECTION1_SCREEN_ORDER[i + 1], notice: null }
}

export { emptyMove, isScreenComplete23 }
